import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo } from "@/components/common/Logo";
import { PaymentIcons } from "@/components/common/PaymentIcons";

const COLS = [
  ["Shop", [["Leopard Luxe™ Mask", "/product/leopard-luxe-led-light-therapy-mask"], ["Shop All", "/shop"], ["The Glow Ritual", "/product/ritual"], ["Reviews", "/reviews"]]],
  ["Learn", [["The Science", "/science"], ["Journal", "/blog"], ["About Us", "/about"], ["FAQs", "/faq"]]],
  ["Support", [["Contact", "/contact"], ["Shipping", "/policies/shipping"], ["Refunds", "/policies/refund"], ["Track Order", "/contact"]]],
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      setDone(true);
      setEmail("");
      toast.success("Welcome to the glow list ✦");
    } catch {
      toast.error("Please enter a valid email");
    }
  };

  return (
    <footer className="relative overflow-hidden bg-[#232222] text-[#FFFCFA]" data-testid="footer">
      <div className="mx-auto max-w-[1400px] px-6 pt-20 md:px-12">
        <div className="grid gap-12 border-b border-white/10 pb-16 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <Logo tone="light" testid="footer-logo" />
            <p className="mt-4 max-w-xs text-sm font-light leading-relaxed text-[#F2E8E6]">
              An Australian beauty brand blending science-backed LED technology with modern self-care rituals, helping women feel confident in their skin, at home.
            </p>
            <form onSubmit={subscribe} className="mt-7 flex max-w-sm">
              <input
                data-testid="footer-newsletter-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for 10% off your first order"
                className="w-full border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-[#9A8E88] focus:border-[#FBC4A8] focus:outline-none"
              />
              <button data-testid="footer-newsletter-submit" className="flex-none bg-[#FBC4A8] px-5 text-[#2C2B2C] transition-colors hover:bg-white">
                {done ? <Check className="h-4 w-4" /> : "Join"}
              </button>
            </form>
          </div>
          {COLS.map(([title, links]) => (
            <div key={title}>
              <p className="text-[0.65rem] uppercase tracking-[0.22em] text-[#FBC4A8]">{title}</p>
              <ul className="mt-5 space-y-3">
                {links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} className="text-sm font-light text-[#F2E8E6] transition-colors hover:text-white">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center justify-between gap-6 py-8 md:flex-row">
          <div className="flex flex-col items-center gap-1 md:items-start">
            <p className="text-xs text-[#9A8E88]">© 2026 Aura Glow Wellness Pty Ltd · ABN 39 684 850 940</p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[0.66rem] uppercase tracking-[0.14em] text-[#9A8E88]">
              <Link to="/policies/privacy" className="transition-colors hover:text-white" data-testid="footer-privacy">Privacy</Link>
              <span className="text-white/15">·</span>
              <Link to="/policies/terms" className="transition-colors hover:text-white" data-testid="footer-terms">Terms</Link>
              <span className="text-white/15">·</span>
              <Link to="/policies/shipping" className="transition-colors hover:text-white">Shipping</Link>
              <span className="text-white/15">·</span>
              <Link to="/policies/refund" className="transition-colors hover:text-white">Refunds</Link>
            </div>
          </div>
          <PaymentIcons methods={["Visa", "Mastercard", "Amex", "PayPal", "Apple Pay", "Afterpay"]} className="justify-center" />
          <div className="flex items-center gap-3">
            <a href="https://www.facebook.com/auraglowwellnessAU/" target="_blank" rel="noreferrer"
              data-testid="footer-facebook" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 transition-colors hover:bg-white/10">
              <Facebook className="h-4 w-4" />
            </a>
            <a href="https://www.instagram.com/auraglowwellness/" target="_blank" rel="noreferrer"
              data-testid="footer-instagram" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 transition-colors hover:bg-white/10">
              <Instagram className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      <p className="select-none whitespace-nowrap text-center font-display text-[19vw] font-light leading-[0.7] tracking-tighter text-white/[0.035] pb-4">AURA GLOW</p>
    </footer>
  );
};
